import React, { useEffect, useRef } from "react";
import { projectStatusCodes } from "../../utils/constants";
import {
  calcProgressingBarPercentage,
  calcTargetAmountPercentage,
} from "../../utils/progressingUtil";

type Props = {
  investmentTotalAmount: number;
  targetAmount: number;
  pendingAmount: number;
  nextTargetAmount?: number;
  projectStatusCode: number;
  paymentDueDate: string;
  showPaymentDueDate: boolean;
};

const FundingSummary: React.FC<Props> = (props) => {
  const {
    investmentTotalAmount,
    targetAmount,
    pendingAmount,
    nextTargetAmount,
    projectStatusCode,
    paymentDueDate,
    showPaymentDueDate,
  } = props;
  const progressPointer = useRef<HTMLDivElement>(null);
  const progress = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const percentage = calcTargetAmountPercentage(
      investmentTotalAmount,
      nextTargetAmount || targetAmount
    );

    if (!progressPointer.current || !progress.current) return;

    progressPointer.current.style.left = `min(calc(${percentage}% - 3px), calc(100% - 3px))`;

    if (percentage > 82) {
      progress.current.style.right = "0";
    } else {
      progress.current.style.left = `clamp(0%, calc(${percentage}% - 30px), 75%)`;
    }
  }, [investmentTotalAmount, nextTargetAmount, targetAmount]);

  return (
    <>
      <dl className="mb-[10px] text-center sm:mb-6">
        <dt className="font-bold">現在の支援金額</dt>
        <dd className="text-[34px] font-bold">
          {investmentTotalAmount.toLocaleString()}
          <span className="text-xl">円</span>
        </dd>
        {projectStatusCode === projectStatusCodes.PROJECT_STATUS_END &&
          pendingAmount > 0 &&
          showPaymentDueDate && (
            <div className="text-xl">
              {`（${paymentDueDate} 最終金額確定予定）`}
            </div>
          )}
      </dl>
      {nextTargetAmount ? (
        <>
          <dl className="flex justify-center">
            <dt className="mr-1 text-[#768084]">NEXT GOAL</dt>
            <dd>{nextTargetAmount.toLocaleString()}円</dd>
          </dl>
          <dl className="flex justify-center">
            <dt className="mr-1 text-[#768084]">(第一目標金額</dt>
            <dd>{targetAmount.toLocaleString()}円)</dd>
          </dl>
        </>
      ) : (
        <dl className="flex justify-center">
          <dt className="mr-1 text-[#768084]">目標金額</dt>
          <dd>{targetAmount.toLocaleString()}円</dd>
        </dl>
      )}
      <div className="relative mt-9 sm:mt-[50px]">
        <div
          className="absolute top-[-30px] inline-block font-raleway text-sm"
          ref={progress}
        >
          {calcTargetAmountPercentage(investmentTotalAmount, targetAmount)}% Funded
        </div>
        <div
          className="absolute bottom-[-6px] top-[-10px] inline-block h-0 w-0 border-l-[3px] border-r-[3px] border-t-[10px] border-l-transparent border-r-transparent border-t-[#1F2122]"
          ref={progressPointer}
        ></div>
        <div className="relative flex h-[6px] items-center bg-[#D2DFE6]">
          <div
            className="h-[8px] bg-[#1F2122]"
            style={{
              width: `${calcProgressingBarPercentage(
                investmentTotalAmount,
                targetAmount,
                nextTargetAmount
              )[0]}%`,
            }}
          ></div>
          {nextTargetAmount && (
            <div
              className="absolute top-[-1px] h-[8px] bg-[#768084]"
              style={{
                width: `${calcProgressingBarPercentage(
                  investmentTotalAmount,
                  targetAmount,
                  nextTargetAmount
                )[1]}%`,
                left: `${calcProgressingBarPercentage(
                  investmentTotalAmount,
                  targetAmount,
                  nextTargetAmount
                )[0]}%`,
              }}
            ></div>
          )}
        </div>
      </div>
    </>
  );
};

export default FundingSummary;
